import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PrivateLayout from '../components/PrivateLayout';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import { LangContext } from '../App';
import api from '../services/api';

const OR = 'var(--couleur-or)';

const T = {
  fr: {
    title: 'Mes crédits',
    retour: '← Tableau de bord',
    solde: 'Solde actuel',
    credits: 'crédits',
    abonnement: 'Abonnement',
    standard: 'Standard',
    premium: 'Premium',
    premium_plus: 'Premium Plus',
    pro: 'Pro',
    expireLE: 'Expire le',
    active: 'Actif',
    acheterPremium: 'Passer à Premium',
    acheterPremiumPlus: 'Passer à Premium Plus',
    prixPremium: (n) => `${n} crédits / an`,
    prixPremiumPlus: (n) => `${n} crédits / an`,
    achatEnCours: 'Traitement…',
    achatSucces: 'Abonnement activé !',
    achatErreur: (msg) => `Erreur : ${msg}`,
    insuffisant: 'Solde insuffisant',
    historique: 'Historique des transactions',
    aucuneTransaction: "Aucune transaction pour l'instant.",
    chargement: 'Chargement…',
    erreur: 'Impossible de charger vos crédits.',
    motifs: {
      observation_validee: 'Observation / validation',
      reduction_location: 'Achat abonnement',
      bonus_admin: 'Bonus admin',
      ajustement_manuel: 'Ajustement manuel',
    },
    page: (p, t) => `Page ${p} sur ${t}`,
    precedent: '← Précédent',
    suivant: 'Suivant →',
    soumettre: 'Soumettre une observation',
    commentGagner: 'Comment gagner des crédits ?',
    regle1: '1 crédit — Publier une observation faunique géoréférencée',
    regle2: '5 crédits — Votre observation est validée par un pair à moins de 500 m',
    regle3: '10 crédits — Une chasse réussie est prouvée sur votre spot',
    regle4: '1 crédit — Chaque photo de chasse personnelle géoréférencée',
  },
  en: {
    title: 'My credits',
    retour: '← Dashboard',
    solde: 'Current balance',
    credits: 'credits',
    abonnement: 'Subscription',
    standard: 'Standard',
    premium: 'Premium',
    premium_plus: 'Premium Plus',
    pro: 'Pro',
    expireLE: 'Expires on',
    active: 'Active',
    acheterPremium: 'Upgrade to Premium',
    acheterPremiumPlus: 'Upgrade to Premium Plus',
    prixPremium: (n) => `${n} credits / year`,
    prixPremiumPlus: (n) => `${n} credits / year`,
    achatEnCours: 'Processing…',
    achatSucces: 'Subscription activated!',
    achatErreur: (msg) => `Error: ${msg}`,
    insuffisant: 'Insufficient balance',
    historique: 'Transaction history',
    aucuneTransaction: 'No transactions yet.',
    chargement: 'Loading…',
    erreur: 'Could not load your credits.',
    motifs: {
      observation_validee: 'Observation / validation',
      reduction_location: 'Subscription purchase',
      bonus_admin: 'Admin bonus',
      ajustement_manuel: 'Manual adjustment',
    },
    page: (p, t) => `Page ${p} of ${t}`,
    precedent: '← Previous',
    suivant: 'Next →',
    soumettre: 'Submit an observation',
    commentGagner: 'How to earn credits?',
    regle1: '1 credit — Post a georeferenced wildlife observation',
    regle2: '5 credits — Your observation is validated by a peer within 500 m',
    regle3: '10 credits — A successful hunt is proven on your spot',
    regle4: '1 credit — Each georeferenced personal hunt photo',
  },
};

const STYLE_CHARGEMENT = {
  color: 'rgba(255,255,255,0.35)',
  fontSize: '0.85rem',
  padding: '1.2rem',
  textAlign: 'center',
  background: 'rgba(255,255,255,0.02)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '8px',
};

const STYLE_ERREUR = {
  background: 'rgba(200,50,50,0.1)',
  border: '1px solid rgba(200,50,50,0.35)',
  borderRadius: '8px',
  padding: '0.8rem 1rem',
  color: 'var(--couleur-texte-erreur)',
  fontSize: '0.85rem',
};

const TITRE_SECTION = {
  fontSize: '0.78rem',
  fontWeight: 700,
  color: 'rgba(255,255,255,0.45)',
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  margin: '0 0 0.9rem',
};

function libelleMotif(t, reason) {
  return t.motifs[reason] || reason;
}

function NiveauBadge({ level, t }) {
  const styles = {
    standard: {
      bg: 'rgba(255,255,255,0.06)',
      border: 'rgba(255,255,255,0.15)',
      color: 'rgba(255,255,255,0.6)',
    },
    premium: {
      bg: 'rgba(201,162,39,0.12)',
      border: 'rgba(201,162,39,0.4)',
      color: 'var(--couleur-or)',
    },
    premium_plus: {
      bg: 'rgba(100,160,255,0.12)',
      border: 'rgba(100,160,255,0.4)',
      color: '#80b0ff',
    },
    pro: { bg: 'rgba(160,80,255,0.12)', border: 'rgba(160,80,255,0.4)', color: '#c080ff' },
  };
  const s = styles[level] || styles.standard;
  const label = t[level] || level;
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: '0.72rem',
        fontWeight: 700,
        background: s.bg,
        border: `1px solid ${s.border}`,
        color: s.color,
        borderRadius: '20px',
        padding: '3px 10px',
        letterSpacing: '0.04em',
      }}
    >
      {label}
    </span>
  );
}

export default function MesCredits() {
  const { utilisateur } = useAuth();
  const { lang } = useContext(LangContext);
  const t = T[lang];

  const [credits, setCredits] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(false);
  const [page, setPage] = useState(1);

  const [achat, setAchat] = useState(null); // null | 'en_cours' | 'succes' | string(erreur)
  const [niveauChoisi, setNiveau] = useState(null);

  // Config publique (prix en crédits)
  const [config, setConfig] = useState({});

  useEffect(() => {
    api
      .get('/public/config')
      .then((r) => setConfig(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let annule = false;
    setChargement(true);
    api
      .get('/me/credits', { params: { page, limite: 15 } })
      .then((r) => {
        if (annule) return;
        setCredits(r.data);
        setErreur(false);
      })
      .catch(() => {
        if (!annule) setErreur(true);
      })
      .finally(() => {
        if (!annule) setChargement(false);
      });
    return () => {
      annule = true;
    };
  }, [page]);

  async function acheterAbonnement(level) {
    setNiveau(level);
    setAchat('en_cours');
    try {
      await api.post('/me/credits/acheter-abonnement', { level });
      setAchat('succes');
      // Recharger les crédits
      const r = await api.get('/me/credits', { params: { page: 1, limite: 15 } });
      setCredits(r.data);
      setPage(1);
    } catch (err) {
      const msg = err?.response?.data?.error || err.message;
      setAchat(msg);
    }
  }

  const solde = credits?.credits_balance ?? 0;
  const niveauAbo = credits?.subscription_tier || utilisateur?.subscription_tier || 'standard';
  const expireLE = credits?.subscription_expires_at;
  const transactions = credits?.data ?? [];
  const totalPages = credits?.pages ?? 1;

  const prixPremium = config.prix_premium_credits_annuel ?? 1200;
  const prixPremiumPlus = config.prix_premium_plus_credits_annuel ?? 3000;

  return (
    <PrivateLayout>
      <div style={{ maxWidth: 880, margin: '0 auto', paddingBottom: '4rem' }}>
        <PageHeader icon="🏆" title={t.title} />

        {chargement && <div style={STYLE_CHARGEMENT}>{t.chargement}</div>}
        {!chargement && erreur && <div style={STYLE_ERREUR}>⚠ {t.erreur}</div>}

        {!chargement && !erreur && (
          <>
            {/* ── Solde + abonnement ── */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: '1rem',
                marginBottom: '2.5rem',
              }}
            >
              {/* Solde */}
              <div
                style={{
                  background: 'rgba(201,162,39,0.08)',
                  border: '1px solid rgba(201,162,39,0.25)',
                  borderRadius: '12px',
                  padding: '1.5rem 1.8rem',
                }}
              >
                <div
                  style={{
                    fontSize: '0.68rem',
                    color: 'rgba(255,255,255,0.4)',
                    letterSpacing: '0.08em',
                    marginBottom: '0.5rem',
                  }}
                >
                  {t.solde.toUpperCase()}
                </div>
                <div style={{ fontSize: '2.8rem', fontWeight: 900, color: OR, lineHeight: 1 }}>
                  {solde.toLocaleString()}
                </div>
                <div
                  style={{
                    fontSize: '0.78rem',
                    color: 'rgba(255,255,255,0.4)',
                    marginTop: '0.3rem',
                  }}
                >
                  {t.credits}
                </div>
              </div>

              {/* Abonnement */}
              <div
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  padding: '1.5rem 1.8rem',
                }}
              >
                <div
                  style={{
                    fontSize: '0.68rem',
                    color: 'rgba(255,255,255,0.4)',
                    letterSpacing: '0.08em',
                    marginBottom: '0.6rem',
                  }}
                >
                  {t.abonnement.toUpperCase()}
                </div>
                <NiveauBadge level={niveauAbo} t={t} />
                {expireLE && (
                  <div
                    style={{
                      fontSize: '0.74rem',
                      color: 'rgba(255,255,255,0.35)',
                      marginTop: '0.5rem',
                    }}
                  >
                    {t.expireLE} :{' '}
                    {new Date(expireLE).toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA')}
                  </div>
                )}
              </div>
            </div>

            {/* ── Acheter un abonnement ── */}
            {(niveauAbo === 'standard' || niveauAbo === 'premium') && (
              <div style={{ marginBottom: '2.5rem' }}>
                <h2 style={TITRE_SECTION}>🎖 {t.abonnement}</h2>

                {achat === 'succes' && (
                  <div
                    style={{
                      background: 'rgba(40,160,80,0.12)',
                      border: '1px solid rgba(40,160,80,0.35)',
                      borderRadius: '8px',
                      padding: '0.8rem 1rem',
                      color: 'var(--couleur-texte-succes)',
                      fontSize: '0.85rem',
                      marginBottom: '1rem',
                    }}
                  >
                    ✓ {t.achatSucces}
                  </div>
                )}
                {achat && achat !== 'en_cours' && achat !== 'succes' && (
                  <div style={{ ...STYLE_ERREUR, marginBottom: '1rem' }}>
                    ⚠ {t.achatErreur(achat)}
                  </div>
                )}

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                    gap: '0.9rem',
                  }}
                >
                  {/* Premium */}
                  {niveauAbo === 'standard' && (
                    <div
                      style={{
                        background: 'rgba(201,162,39,0.07)',
                        border: '1px solid rgba(201,162,39,0.25)',
                        borderRadius: '10px',
                        padding: '1.2rem 1.4rem',
                      }}
                    >
                      <div style={{ fontWeight: 700, marginBottom: '0.4rem', color: OR }}>
                        Premium
                      </div>
                      <div
                        style={{
                          fontSize: '0.8rem',
                          color: 'rgba(255,255,255,0.5)',
                          marginBottom: '1rem',
                        }}
                      >
                        {t.prixPremium(prixPremium)}
                      </div>
                      <button
                        onClick={() => acheterAbonnement('premium')}
                        disabled={achat === 'en_cours' || solde < prixPremium}
                        style={{
                          background: solde >= prixPremium ? OR : 'rgba(255,255,255,0.08)',
                          color: solde >= prixPremium ? '#1a2e1a' : 'rgba(255,255,255,0.3)',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '0.5rem 1rem',
                          fontSize: '0.82rem',
                          fontWeight: 700,
                          cursor:
                            solde >= prixPremium && achat !== 'en_cours'
                              ? 'pointer'
                              : 'not-allowed',
                          width: '100%',
                        }}
                      >
                        {achat === 'en_cours' && niveauChoisi === 'premium'
                          ? t.achatEnCours
                          : solde < prixPremium
                            ? t.insuffisant
                            : t.acheterPremium}
                      </button>
                    </div>
                  )}

                  {/* Premium Plus */}
                  {(niveauAbo === 'standard' || niveauAbo === 'premium') && (
                    <div
                      style={{
                        background: 'rgba(100,160,255,0.07)',
                        border: '1px solid rgba(100,160,255,0.22)',
                        borderRadius: '10px',
                        padding: '1.2rem 1.4rem',
                      }}
                    >
                      <div style={{ fontWeight: 700, marginBottom: '0.4rem', color: '#80b0ff' }}>
                        Premium Plus
                      </div>
                      <div
                        style={{
                          fontSize: '0.8rem',
                          color: 'rgba(255,255,255,0.5)',
                          marginBottom: '1rem',
                        }}
                      >
                        {t.prixPremiumPlus(prixPremiumPlus)}
                      </div>
                      <button
                        onClick={() => acheterAbonnement('premium_plus')}
                        disabled={achat === 'en_cours' || solde < prixPremiumPlus}
                        style={{
                          background:
                            solde >= prixPremiumPlus ? '#80b0ff' : 'rgba(255,255,255,0.08)',
                          color: solde >= prixPremiumPlus ? '#0a1a2e' : 'rgba(255,255,255,0.3)',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '0.5rem 1rem',
                          fontSize: '0.82rem',
                          fontWeight: 700,
                          cursor:
                            solde >= prixPremiumPlus && achat !== 'en_cours'
                              ? 'pointer'
                              : 'not-allowed',
                          width: '100%',
                        }}
                      >
                        {achat === 'en_cours' && niveauChoisi === 'premium_plus'
                          ? t.achatEnCours
                          : solde < prixPremiumPlus
                            ? t.insuffisant
                            : t.acheterPremiumPlus}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Comment gagner des crédits ── */}
            <div style={{ marginBottom: '2.5rem' }}>
              <h2 style={TITRE_SECTION}>💡 {t.commentGagner}</h2>
              <div
                style={{
                  background: 'rgba(255,255,255,0.025)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '10px',
                  padding: '1rem 1.3rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.6rem',
                }}
              >
                {[t.regle1, t.regle2, t.regle3, t.regle4].map((r, i) => (
                  <div
                    key={i}
                    style={{
                      fontSize: '0.84rem',
                      color: 'rgba(255,255,255,0.62)',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.6rem',
                    }}
                  >
                    <span style={{ color: OR, fontWeight: 700, flexShrink: 0 }}>+</span>
                    {r}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '0.8rem', textAlign: 'right' }}>
                <Link
                  to="/submit-observation"
                  style={{
                    fontSize: '0.82rem',
                    color: OR,
                    textDecoration: 'none',
                    fontWeight: 600,
                  }}
                >
                  {t.soumettre} →
                </Link>
              </div>
            </div>

            {/* ── Historique des transactions ── */}
            <div>
              <h2 style={TITRE_SECTION}>📋 {t.historique}</h2>

              {transactions.length === 0 && (
                <div
                  style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px dashed rgba(184,134,11,0.3)',
                    borderRadius: '8px',
                    padding: '1.4rem',
                    textAlign: 'center',
                    color: 'rgba(255,255,255,0.35)',
                    fontSize: '0.88rem',
                  }}
                >
                  {t.aucuneTransaction}
                </div>
              )}

              {transactions.length > 0 && (
                <>
                  <div
                    style={{
                      background: 'rgba(255,255,255,0.02)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '10px',
                      overflow: 'hidden',
                    }}
                  >
                    {transactions.map((tx, i) => {
                      const positif = tx.credits_amount > 0;
                      const date = new Date(tx.createdAt).toLocaleDateString(
                        lang === 'fr' ? 'fr-CA' : 'en-CA'
                      );
                      return (
                        <div
                          key={tx.id}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr auto auto',
                            gap: '1rem',
                            padding: '0.8rem 1.1rem',
                            borderBottom:
                              i < transactions.length - 1
                                ? '1px solid rgba(255,255,255,0.05)'
                                : 'none',
                            alignItems: 'center',
                            background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)',
                          }}
                        >
                          <div>
                            <div style={{ fontSize: '0.84rem', color: 'var(--couleur-texte)' }}>
                              {libelleMotif(t, tx.reason)}
                            </div>
                            {tx.description && (
                              <div
                                style={{
                                  fontSize: '0.72rem',
                                  color: 'rgba(255,255,255,0.35)',
                                  marginTop: '0.15rem',
                                }}
                              >
                                {tx.description}
                              </div>
                            )}
                          </div>
                          <div
                            style={{
                              fontSize: '0.74rem',
                              color: 'rgba(255,255,255,0.32)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {date}
                          </div>
                          <div
                            style={{
                              fontWeight: 800,
                              fontSize: '0.95rem',
                              color: positif
                                ? 'var(--couleur-texte-succes)'
                                : 'var(--couleur-texte-erreur)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {positif ? '+' : ''}
                            {tx.credits_amount}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '1rem',
                        marginTop: '1rem',
                      }}
                    >
                      <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1}
                        style={{
                          background: 'rgba(255,255,255,0.06)',
                          border: '1px solid rgba(255,255,255,0.15)',
                          color: page <= 1 ? 'rgba(255,255,255,0.25)' : 'var(--couleur-texte)',
                          borderRadius: '6px',
                          padding: '0.4rem 0.9rem',
                          fontSize: '0.82rem',
                          cursor: page <= 1 ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {t.precedent}
                      </button>
                      <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.38)' }}>
                        {t.page(page, totalPages)}
                      </span>
                      <button
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages}
                        style={{
                          background: 'rgba(255,255,255,0.06)',
                          border: '1px solid rgba(255,255,255,0.15)',
                          color:
                            page >= totalPages ? 'rgba(255,255,255,0.25)' : 'var(--couleur-texte)',
                          borderRadius: '6px',
                          padding: '0.4rem 0.9rem',
                          fontSize: '0.82rem',
                          cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {t.suivant}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </PrivateLayout>
  );
}
